import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Package, MapPin, Calendar, IndianRupee, ArrowLeft,
  CheckCircle2, Circle, Truck, Home, CreditCard,
  XCircle, RefreshCw, Clock, Star, AlertCircle,
  Sparkles, Copy, ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

/* ─── Status helpers ───────────────────────────────────────── */
const STATUS_META = {
  pending:    { label: 'Pending',    icon: Clock,        bg: 'rgba(251,191,36,.15)',  border: 'rgba(251,191,36,.35)', text: '#92400e', dot: '#f59e0b' },
  confirmed:  { label: 'Confirmed',  icon: CheckCircle2, bg: 'rgba(59,130,246,.12)',  border: 'rgba(59,130,246,.35)', text: '#1e40af', dot: '#3b82f6' },
  processing: { label: 'Processing', icon: RefreshCw,    bg: 'rgba(168,85,247,.12)',  border: 'rgba(168,85,247,.35)', text: '#5b21b6', dot: '#a855f7' },
  shipped:    { label: 'Shipped',    icon: Truck,        bg: 'rgba(99,102,241,.12)',  border: 'rgba(99,102,241,.35)', text: '#312e81', dot: '#6366f1' },
  delivered:  { label: 'Delivered',  icon: CheckCircle2, bg: 'rgba(16,185,129,.12)',  border: 'rgba(16,185,129,.35)', text: '#064e3b', dot: '#10b981' },
  cancelled:  { label: 'Cancelled',  icon: XCircle,      bg: 'rgba(239,68,68,.10)',   border: 'rgba(239,68,68,.35)',  text: '#7f1d1d', dot: '#ef4444' },
  refunded:   { label: 'Refunded',   icon: RefreshCw,    bg: 'rgba(107,114,128,.12)', border: 'rgba(107,114,128,.35)',text: '#374151', dot: '#9ca3af' },
  paid:       { label: 'Paid',       icon: CheckCircle2, bg: 'rgba(16,185,129,.12)',  border: 'rgba(16,185,129,.35)', text: '#064e3b', dot: '#10b981' },
};

const StatusBadge = ({ status, large }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: large ? '6px 16px' : '4px 10px',
      borderRadius: 20,
      fontSize: large ? 13 : 11,
      fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase',
      background: m.bg, border: `1px solid ${m.border}`, color: m.text,
    }}>
      <span style={{ width: large ? 8 : 6, height: large ? 8 : 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
};

/* ─── Timeline steps ─────────────────────────────────────── */
const TIMELINE_STEPS = [
  { key: 'pending',    label: 'Order Placed',        sublabel: 'We received your order',    icon: Circle       },
  { key: 'confirmed',  label: 'Payment Confirmed',   sublabel: 'Payment successfully done', icon: CheckCircle2 },
  { key: 'processing', label: 'Being Prepared',      sublabel: 'Items packed & labeled',    icon: Package      },
  { key: 'shipped',    label: 'Out for Delivery',    sublabel: 'On its way to you',         icon: Truck        },
  { key: 'delivered',  label: 'Delivered',           sublabel: 'Package received 🎉',       icon: Home         },
];
const STATUS_ORDER = TIMELINE_STEPS.map(s => s.key);

/* ─── Page ──────────────────────────────────────────────── */
const OrderDetailsPage = () => {
  const { orderIdentifier } = useParams();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fetchedRef, setFetchedRef] = useState(false);

  useEffect(() => {
    if (user && session && !fetchedRef) {
      setFetchedRef(true);
      fetchOrderDetails();
    }
  }, [user, session, orderIdentifier]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/orders/${orderIdentifier}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const result = await response.json();
      if (result.success) setOrder(result.order);
      else setError(result.error);
    } catch (err) {
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) { alert('Please provide a reason for cancellation'); return; }
    try {
      setCancelling(true);
      const response = await fetch(`${API_URL}/api/orders/${order.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, reason: cancelReason })
      });
      const result = await response.json();
      if (result.success) {
        setShowCancelModal(false);
        fetchOrderDetails();
      } else {
        alert(result.error || 'Failed to cancel order');
      }
    } catch (err) {
      alert('Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const copyOrderNum = () => {
    if (order?.order_number) {
      navigator.clipboard.writeText(order.order_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const canCancel = status => !['shipped', 'delivered', 'cancelled', 'refunded'].includes(status);

  /* ── guards ── */
  if (!user) { navigate('/login'); return null; }

  if (loading) return (
    <div style={s.pageWrap}>
      <div style={s.orb1} /><div style={s.orb2} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <div style={s.spinner} />
        <p style={{ color: '#64748b', marginTop: 20, fontWeight: 500 }}>Loading order details…</p>
      </div>
    </div>
  );

  if (error || !order) return (
    <div style={s.pageWrap}>
      <div style={s.orb1} /><div style={s.orb2} />
      <div style={{ ...s.glassCard, maxWidth: 440, margin: '100px auto', textAlign: 'center', padding: 48 }}>
        <div style={{ ...s.iconCircle, background: 'rgba(239,68,68,.1)', marginBottom: 20 }}>
          <XCircle size={40} color="#dc2626" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: '0 0 10px' }}>Order Not Found</h2>
        <p style={{ color: '#64748b', marginBottom: 24 }}>{error || 'The order you are looking for does not exist.'}</p>
        <Link to="/orders" style={s.primaryBtn}>View All Orders</Link>
      </div>
    </div>
  );

  /* ── Timeline ── */
  const currentIdx = STATUS_ORDER.indexOf(order.status);
  const isCancelledState = ['cancelled', 'refunded'].includes(order.status);

  const fmt = (dateStr, opts) => new Date(dateStr).toLocaleDateString('en-IN', opts || { day: 'numeric', month: 'long', year: 'numeric' });
  const fmtFull = dateStr => new Date(dateStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div style={s.pageWrap}>
      <div style={s.orb1} /><div style={s.orb2} /><div style={s.orb3} />

      <div style={s.container}>

        {/* ── Back ── */}
        <button onClick={() => navigate('/orders')} style={s.backBtn}>
          <ArrowLeft size={16} /> Back to Orders
        </button>

        {/* ── Hero Header Card ── */}
        <div style={s.heroCard}>
          <div style={s.heroStripe} />
          <div style={s.heroContent}>
            <div style={s.heroLeft}>
              <div style={s.headerBadge}>
                <Sparkles size={12} /> Order Details
              </div>
              <div style={s.orderNumRow}>
                <h1 style={s.orderNum}>{order.order_number}</h1>
                <button onClick={copyOrderNum} style={s.copyBtn} title="Copy order number">
                  {copied ? <CheckCircle2 size={14} color="#10b981" /> : <Copy size={14} />}
                </button>
              </div>
              <p style={s.orderDate}>
                <Calendar size={13} style={{ verticalAlign: 'middle' }} />
                {' '}Placed on {fmt(order.created_at, { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
            <div style={s.heroRight}>
              <StatusBadge status={order.status} large />
              <div style={s.heroAmount}>
                <div style={s.heroAmountLabel}>Order Total</div>
                <div style={s.heroAmountVal}>
                  <IndianRupee size={22} />
                  {parseFloat(order.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>
              {canCancel(order.status) && (
                <button onClick={() => setShowCancelModal(true)} style={s.cancelBtn}>
                  <XCircle size={14} /> Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Cancelled banner ── */}
        {isCancelledState && (
          <div style={s.cancelledBanner}>
            <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, color: '#7f1d1d', marginBottom: 2 }}>
                Order {order.status === 'cancelled' ? 'Cancelled' : 'Refunded'}
              </div>
              <div style={{ fontSize: 13, color: '#991b1b' }}>
                {order.customer_notes || order.admin_notes || 'This order has been cancelled.'}
              </div>
            </div>
          </div>
        )}

        {/* ── Status Timeline ── */}
        {!isCancelledState && (
          <div style={s.timelineCard}>
            <h2 style={s.sectionTitle}>Order Journey</h2>
            <div style={s.timelineWrap}>
              {TIMELINE_STEPS.map((step, idx) => {
                const done = idx <= currentIdx;
                const active = idx === currentIdx;
                const Icon = step.icon;
                return (
                  <div key={step.key} style={s.timelineItem}>
                    {/* connector line */}
                    {idx < TIMELINE_STEPS.length - 1 && (
                      <div style={{
                        ...s.connector,
                        background: done && idx < currentIdx
                          ? 'linear-gradient(90deg,#10b981,#6366f1)'
                          : 'rgba(226,232,240,1)',
                      }} />
                    )}
                    {/* node */}
                    <div style={{
                      ...s.timelineNode,
                      background: done
                        ? active
                          ? 'linear-gradient(135deg,#f97316,#7c3aed)'
                          : 'linear-gradient(135deg,#10b981,#059669)'
                        : 'rgba(226,232,240,1)',
                      boxShadow: active ? '0 0 0 4px rgba(249,115,22,.2)' : done ? '0 0 0 3px rgba(16,185,129,.15)' : 'none',
                      transform: active ? 'scale(1.15)' : 'scale(1)',
                    }}>
                      <Icon size={14} color={done ? '#fff' : '#94a3b8'} />
                    </div>
                    {/* label */}
                    <div style={s.timelineLabel}>
                      <div style={{ fontSize: 12, fontWeight: done ? 700 : 500, color: done ? '#1e293b' : '#94a3b8', marginTop: 8 }}>
                        {step.label}
                      </div>
                      <div style={{ fontSize: 10, color: done ? '#64748b' : '#cbd5e1', marginTop: 2 }}>
                        {step.sublabel}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Main Grid ── */}
        <div style={s.mainGrid}>

          {/* ── LEFT: Items + Shipping ── */}
          <div style={s.leftCol}>

            {/* Order Items */}
            <div style={s.glassCard}>
              <div style={s.cardHeader}>
                <div style={s.cardIconWrap}><Package size={16} color="#f97316" /></div>
                <h2 style={s.sectionTitle}>Order Items</h2>
              </div>
              <div style={s.itemsList}>
                {order.items?.map((item, idx) => (
                  <div key={idx} style={{ ...s.itemRow, borderBottom: idx < order.items.length - 1 ? '1px solid rgba(226,232,240,.8)' : 'none' }}>
                    <div style={s.itemImgWrap}>
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.product_name}
                          style={s.itemImg}
                          onError={e => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{ ...s.itemImgFallback, display: item.image_url ? 'none' : 'flex' }}>
                        <Package size={22} color="#cbd5e1" />
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={s.itemName}>{item.product_name}</h3>
                      {item.variant_name && <p style={s.itemVariant}>{item.variant_name}</p>}
                      {item.sku && <p style={s.itemSku}>SKU: {item.sku}</p>}
                      <div style={s.itemFooter}>
                        <span style={s.itemQty}>Qty: {item.quantity}</span>
                        <span style={s.itemPrice}>
                          <IndianRupee size={13} />
                          {parseFloat(item.total_price).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Address */}
            {order.customer_addresses && (
              <div style={s.glassCard}>
                <div style={s.cardHeader}>
                  <div style={s.cardIconWrap}><MapPin size={16} color="#f97316" /></div>
                  <h2 style={s.sectionTitle}>Delivery Address</h2>
                </div>
                <div style={s.addressBlock}>
                  <p style={s.addressName}>{order.customer_addresses.full_name}</p>
                  <p style={s.addressLine}>{order.customer_addresses.address_line1}</p>
                  {order.customer_addresses.address_line2 && <p style={s.addressLine}>{order.customer_addresses.address_line2}</p>}
                  <p style={s.addressLine}>
                    {order.customer_addresses.city}, {order.customer_addresses.state}
                    {order.customer_addresses.pincode ? ` - ${order.customer_addresses.pincode}` : ''}
                  </p>
                  <p style={s.addressLine}>{order.customer_addresses.country}</p>
                  <div style={s.addressPhone}>
                    📞 {order.customer_addresses.phone}
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Info */}
            {(order.courier_name || order.tracking_number) && (
              <div style={s.glassCard}>
                <div style={s.cardHeader}>
                  <div style={s.cardIconWrap}><Truck size={16} color="#f97316" /></div>
                  <h2 style={s.sectionTitle}>Shipment Info</h2>
                </div>
                <div style={s.shippingGrid}>
                  {order.courier_name && (
                    <div style={s.shippingItem}>
                      <div style={s.shippingLabel}>Courier</div>
                      <div style={s.shippingValue}>{order.courier_name}</div>
                    </div>
                  )}
                  {order.tracking_number && (
                    <div style={s.shippingItem}>
                      <div style={s.shippingLabel}>Tracking No.</div>
                      <div style={{ ...s.shippingValue, fontFamily: 'monospace', fontSize: 13, color: '#6366f1' }}>
                        {order.tracking_number}
                      </div>
                    </div>
                  )}
                  {order.shipped_at && (
                    <div style={s.shippingItem}>
                      <div style={s.shippingLabel}>Shipped On</div>
                      <div style={s.shippingValue}>{fmtFull(order.shipped_at)}</div>
                    </div>
                  )}
                  {order.estimated_delivery_date && (
                    <div style={s.shippingItem}>
                      <div style={s.shippingLabel}>Est. Delivery</div>
                      <div style={{ ...s.shippingValue, color: '#059669', fontWeight: 700 }}>
                        {fmt(order.estimated_delivery_date)}
                      </div>
                    </div>
                  )}
                  {order.delivered_at && (
                    <div style={{ ...s.shippingItem, gridColumn: '1/-1', borderTop: '1px dashed rgba(16,185,129,.3)', paddingTop: 12 }}>
                      <div style={s.shippingLabel}>Delivered On</div>
                      <div style={{ ...s.shippingValue, color: '#10b981', fontWeight: 700 }}>✓ {fmtFull(order.delivered_at)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: Payment summary ── */}
          <div style={s.rightCol}>

            {/* Payment Summary */}
            <div style={{ ...s.glassCard, position: 'sticky', top: 100 }}>
              <div style={s.cardHeader}>
                <div style={s.cardIconWrap}><CreditCard size={16} color="#f97316" /></div>
                <h2 style={s.sectionTitle}>Payment</h2>
              </div>

              <div style={s.summaryRows}>
                <div style={s.summaryRow}>
                  <span style={s.summaryKey}>Subtotal</span>
                  <span style={s.summaryVal}>₹{parseFloat(order.subtotal).toLocaleString('en-IN')}</span>
                </div>
                <div style={s.summaryRow}>
                  <span style={s.summaryKey}>GST</span>
                  <span style={s.summaryVal}>₹{parseFloat(order.tax_amount).toLocaleString('en-IN')}</span>
                </div>
                {parseFloat(order.discount_amount) > 0 && (
                  <div style={{ ...s.summaryRow, color: '#059669' }}>
                    <span style={{ ...s.summaryKey, color: '#059669' }}>Discount</span>
                    <span style={{ ...s.summaryVal, color: '#059669', fontWeight: 700 }}>
                      −₹{parseFloat(order.discount_amount).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                {parseFloat(order.shipping_charge) > 0 && (
                  <div style={s.summaryRow}>
                    <span style={s.summaryKey}>Shipping</span>
                    <span style={s.summaryVal}>₹{parseFloat(order.shipping_charge).toLocaleString('en-IN')}</span>
                  </div>
                )}
                {parseFloat(order.shipping_charge) === 0 && (
                  <div style={s.summaryRow}>
                    <span style={s.summaryKey}>Shipping</span>
                    <span style={{ ...s.summaryVal, color: '#059669', fontWeight: 700 }}>FREE</span>
                  </div>
                )}
              </div>

              <div style={s.totalRow}>
                <span style={s.totalKey}>Total</span>
                <span style={s.totalVal}>
                  <IndianRupee size={18} />
                  {parseFloat(order.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* Payment status pill */}
              <div style={s.paymentStatusRow}>
                <StatusBadge status={order.payment_status} />
                {order.payment_method && (
                  <span style={s.payMethodTag}>
                    {order.payment_method.replace(/_/g, ' ').toUpperCase()}
                  </span>
                )}
              </div>

              {order.razorpay_payment_id && (
                <div style={s.payIdRow}>
                  <span style={s.payIdLabel}>Payment ID</span>
                  <span style={s.payIdVal}>{order.razorpay_payment_id}</span>
                </div>
              )}

              {/* upsell nudge */}
              <Link to="/" style={s.shopMoreBtn}>
                <Star size={14} /> Shop More
                <ChevronRight size={14} style={{ marginLeft: 'auto' }} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Cancel Modal ── */}
      {showCancelModal && (
        <div style={s.modalOverlay}>
          <div style={s.modalCard}>
            <div style={s.modalHeader}>
              <div style={{ ...s.iconCircle, background: 'rgba(239,68,68,.1)' }}>
                <XCircle size={28} color="#dc2626" />
              </div>
              <h3 style={s.modalTitle}>Cancel Order?</h3>
              <p style={s.modalSubtitle}>
                Are you sure you want to cancel <strong>{order.order_number}</strong>? This action cannot be undone.
              </p>
            </div>
            <textarea
              placeholder="Tell us why you're cancelling…"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              style={s.cancelTextarea}
              rows={3}
            />
            <div style={s.modalActions}>
              <button
                onClick={() => setShowCancelModal(false)}
                style={s.keepBtn}
                disabled={cancelling}
              >Keep Order</button>
              <button
                onClick={handleCancelOrder}
                style={{ ...s.confirmCancelBtn, opacity: cancelling ? .6 : 1 }}
                disabled={cancelling}
              >{cancelling ? 'Cancelling…' : 'Yes, Cancel'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── styles ─────────────────────────────────────────────── */
const s = {
  pageWrap: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fef9f0 0%, #fff7ed 30%, #fdf4ff 60%, #eff6ff 100%)',
    position: 'relative', overflow: 'hidden', paddingBottom: 80,
  },
  orb1: { position:'fixed', top:-100, right:-100, width:500, height:500, borderRadius:'50%', background:'radial-gradient(circle,rgba(251,146,60,.18) 0%,transparent 70%)', pointerEvents:'none', zIndex:0 },
  orb2: { position:'fixed', bottom:-150, left:-100, width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle,rgba(168,85,247,.12) 0%,transparent 70%)', pointerEvents:'none', zIndex:0 },
  orb3: { position:'fixed', top:'40%', left:'35%', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle,rgba(99,102,241,.08) 0%,transparent 70%)', pointerEvents:'none', zIndex:0 },

  container: { maxWidth: 1000, margin: '0 auto', padding: '36px 20px', position: 'relative', zIndex: 1 },

  spinner: { width:44, height:44, border:'3px solid rgba(249,115,22,.2)', borderTopColor:'#f97316', borderRadius:'50%', animation:'spin .8s linear infinite' },

  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,.8)', backdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(249,115,22,.2)', borderRadius: 12,
    padding: '8px 18px', fontSize: 13, fontWeight: 700, color: '#374151',
    cursor: 'pointer', marginBottom: 24, transition: 'all .2s',
  },

  /* hero */
  heroCard: {
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.8)', borderRadius: 24,
    overflow: 'hidden', marginBottom: 20,
    boxShadow: '0 8px 40px rgba(249,115,22,.1)',
    position: 'relative',
  },
  heroStripe: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 4,
    background: 'linear-gradient(90deg,#f97316,#7c3aed,#3b82f6)',
  },
  heroContent: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 20, padding: '28px 28px 24px',
  },
  heroLeft: { flex: 1, minWidth: 220 },
  headerBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: 'rgba(249,115,22,.1)', border: '1px solid rgba(249,115,22,.25)',
    borderRadius: 20, padding: '3px 11px', fontSize: 11,
    fontWeight: 700, color: '#ea580c', marginBottom: 10, letterSpacing: '.5px',
    textTransform: 'uppercase',
  },
  orderNumRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  orderNum: {
    fontSize: 'clamp(16px,3vw,22px)', fontWeight: 900, margin: 0,
    color: '#1e293b', fontFamily: 'monospace',
  },
  copyBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.25)',
    borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#4f46e5',
    transition: 'all .2s',
  },
  orderDate: { fontSize: 13, color: '#64748b', margin: 0, fontWeight: 500 },
  heroRight: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, flexShrink: 0,
  },
  heroAmount: { textAlign: 'right' },
  heroAmountLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 3 },
  heroAmountVal: {
    display: 'flex', alignItems: 'center', gap: 2,
    fontSize: 'clamp(22px,4vw,30px)', fontWeight: 900,
    background: 'linear-gradient(135deg,#1e293b,#7c3aed)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  cancelBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(239,68,68,.08)', border: '1.5px solid rgba(239,68,68,.3)',
    borderRadius: 12, padding: '7px 14px', fontSize: 12, fontWeight: 700,
    color: '#dc2626', cursor: 'pointer', transition: 'all .2s',
  },

  cancelledBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    background: 'rgba(254,242,242,.9)', backdropFilter: 'blur(12px)',
    border: '1px solid rgba(239,68,68,.3)', borderRadius: 16,
    padding: '16px 20px', marginBottom: 20,
  },

  /* timeline */
  timelineCard: {
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.8)', borderRadius: 22,
    padding: '24px 28px', marginBottom: 20,
    boxShadow: '0 4px 24px rgba(0,0,0,.06)',
  },
  timelineWrap: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 0, overflowX: 'auto', paddingBottom: 4,
  },
  timelineItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    flex: 1, minWidth: 80, position: 'relative',
  },
  connector: {
    position: 'absolute', top: 18, left: '50%', width: '100%', height: 3,
    borderRadius: 2, transition: 'background .5s',
  },
  timelineNode: {
    width: 36, height: 36, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all .3s', zIndex: 1, position: 'relative',
  },
  timelineLabel: { textAlign: 'center', maxWidth: 90 },

  /* main grid */
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1.6fr) minmax(0,1fr)',
    gap: 20, alignItems: 'start',
  },
  leftCol: { display: 'flex', flexDirection: 'column', gap: 20 },
  rightCol: { display: 'flex', flexDirection: 'column', gap: 20 },

  /* glass card */
  glassCard: {
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.8)', borderRadius: 22,
    padding: '22px', boxShadow: '0 4px 24px rgba(0,0,0,.06)',
    overflow: 'hidden',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 },
  cardIconWrap: {
    width: 34, height: 34, borderRadius: 10,
    background: 'linear-gradient(135deg,rgba(249,115,22,.15),rgba(249,115,22,.05))',
    border: '1px solid rgba(249,115,22,.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: '#1e293b', margin: 0 },

  /* items */
  itemsList: { display: 'flex', flexDirection: 'column', gap: 0 },
  itemRow: { display: 'flex', gap: 14, padding: '14px 0' },
  itemImgWrap: {
    width: 70, height: 70, flexShrink: 0,
    borderRadius: 12, overflow: 'hidden',
    background: '#f1f5f9', border: '1px solid #e2e8f0',
  },
  itemImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  itemImgFallback: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  itemName: { fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' },
  itemVariant: { fontSize: 12, color: '#64748b', margin: '0 0 2px' },
  itemSku: { fontSize: 11, color: '#94a3b8', margin: '0 0 8px', fontFamily: 'monospace' },
  itemFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  itemQty: { fontSize: 12, color: '#64748b', fontWeight: 600 },
  itemPrice: {
    display: 'flex', alignItems: 'center', gap: 2,
    fontSize: 15, fontWeight: 800, color: '#1e293b',
  },

  /* address */
  addressBlock: { color: '#374151' },
  addressName: { fontWeight: 800, fontSize: 15, margin: '0 0 6px', color: '#1e293b' },
  addressLine: { fontSize: 13, margin: '0 0 3px', color: '#475569' },
  addressPhone: {
    marginTop: 12, fontSize: 13, fontWeight: 600, color: '#374151',
    background: 'rgba(248,250,252,1)', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '8px 12px', display: 'inline-block',
  },

  /* shipping grid */
  shippingGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  shippingItem: {},
  shippingLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 },
  shippingValue: { fontSize: 14, fontWeight: 700, color: '#1e293b' },

  /* payment summary */
  summaryRows: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  summaryKey: { fontSize: 13, color: '#64748b' },
  summaryVal: { fontSize: 13, fontWeight: 600, color: '#1e293b' },
  totalRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderTop: '2px solid rgba(249,115,22,.15)', paddingTop: 14, marginBottom: 14,
  },
  totalKey: { fontSize: 16, fontWeight: 800, color: '#1e293b' },
  totalVal: {
    display: 'flex', alignItems: 'center', gap: 1,
    fontSize: 22, fontWeight: 900,
    background: 'linear-gradient(135deg,#f97316,#7c3aed)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  paymentStatusRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  payMethodTag: {
    fontSize: 11, fontWeight: 700, letterSpacing: '.5px',
    background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.25)',
    color: '#4338ca', borderRadius: 8, padding: '3px 9px',
  },
  payIdRow: { marginBottom: 16 },
  payIdLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600, display: 'block', marginBottom: 3 },
  payIdVal: { fontSize: 12, fontFamily: 'monospace', color: '#374151' },
  shopMoreBtn: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: 'linear-gradient(135deg,rgba(249,115,22,.1),rgba(124,58,237,.08))',
    border: '1.5px solid rgba(249,115,22,.2)', borderRadius: 14,
    padding: '11px 16px', textDecoration: 'none',
    fontSize: 13, fontWeight: 700, color: '#f97316',
    transition: 'all .2s', marginTop: 4,
  },

  iconCircle: {
    width: 70, height: 70, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'linear-gradient(135deg,#f97316,#ea580c)',
    color: '#fff', fontWeight: 700, fontSize: 14,
    padding: '11px 26px', borderRadius: 14,
    textDecoration: 'none', boxShadow: '0 4px 16px rgba(249,115,22,.35)',
  },

  /* modal */
  modalOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 20,
  },
  modalCard: {
    background: 'rgba(255,255,255,.95)', backdropFilter: 'blur(20px)',
    borderRadius: 24, maxWidth: 440, width: '100%',
    padding: 32, boxShadow: '0 25px 80px rgba(0,0,0,.2)',
    border: '1px solid rgba(255,255,255,.8)',
  },
  modalHeader: { textAlign: 'center', marginBottom: 20 },
  modalTitle: { fontSize: 20, fontWeight: 900, color: '#1e293b', margin: '14px 0 6px' },
  modalSubtitle: { fontSize: 13, color: '#64748b', lineHeight: 1.5 },
  cancelTextarea: {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid rgba(239,68,68,.3)', borderRadius: 12,
    fontSize: 14, color: '#1e293b', resize: 'vertical',
    outline: 'none', marginBottom: 16, boxSizing: 'border-box',
    background: 'rgba(254,242,242,.4)',
  },
  modalActions: { display: 'flex', gap: 12 },
  keepBtn: {
    flex: 1, padding: '11px 0', borderRadius: 12,
    border: '1.5px solid #e2e8f0', background: 'transparent',
    fontWeight: 700, fontSize: 14, color: '#374151', cursor: 'pointer',
    transition: 'all .2s',
  },
  confirmCancelBtn: {
    flex: 1, padding: '11px 0', borderRadius: 12,
    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
    border: 'none', fontWeight: 700, fontSize: 14, color: '#fff',
    cursor: 'pointer', boxShadow: '0 4px 16px rgba(239,68,68,.35)',
    transition: 'opacity .2s',
  },
};

/* ── Responsive overrides ─ inject a style tag ── */
const ResponsiveStyle = () => (
  <style>{`
    @media (max-width: 700px) {
      .order-main-grid { grid-template-columns: 1fr !important; }
      .order-timeline-wrap { gap: 2px !important; }
    }
    @keyframes spin { to { transform: rotate(360deg); } }
  `}</style>
);

const WrappedOrderDetailsPage = () => (
  <>
    <ResponsiveStyle />
    <OrderDetailsPage />
  </>
);

export default WrappedOrderDetailsPage;
